import styled, { css } from "styled-components";
import {
  LABELS,
  ScrollToTopOnMount,
  parseCSS,
  parsePrint,
  randomName,
} from "scripts/FunctionsBundle";
import { useEffect, useState } from "react";
import * as _T from "@utils/ClassTypes";
import SacramentosForm from "@components/SacramentosForms";
import { useLoaderData, useNavigate, useParams } from "react-router";
import { Helmet } from "react-helmet";
import { GS } from "App";
import SearchTable from "@components/SearchTable";

// ################################ INTERFACES & PROPS ################################
type _Base = import("@utils/ClassTypes")._Base;
// SacramentosScreen => Rename all instances to use
type SacramentosScreenProps = {} & _Base;

// ################################ RENDERING COMPONENT ################################
const _SacramentosScreen = (props: SacramentosScreenProps) => {
  type a = _T.Bautizo | _T.Comunion | _T.Confirmacion | _T.Matrimonio;
  type aany = _T.Bautizo & _T.Comunion & _T.Confirmacion & _T.Matrimonio;

  const navigate = useNavigate();
  const { type, id } = useParams();
  const { res_all, res_one } = useLoaderData() as {
    res_all: aany[];
    res_one?: aany;
  };

  // ------------------------------------------------------------------------------------ LOCAL STATE
  const [LS, setLS] = useState({ showElement: res_one });

  // ------------------------------------------------------------------------------------ HANDLE CONTROLS
  function handleControls(action: "edit" | "new" | "delete" | "print") {
    if (action === "new") {
      // DEFAULTS
      const def_ciudad = "Hermosillo, Sonora";
      const def_id = randomName("id");

      const t =
        type === "matrimonios"
          ? "Matrimonio"
          : type === "confirmaciones"
          ? "Confirmacion"
          : type === "Comuniones"
          ? "Comunion"
          : "Bautizo";

      let temp: a = new _T[t]({
        id: def_id,
        ciudad_sacramento: def_ciudad,
        ciudad_bautizo: def_ciudad,
        ciudad_nacimiento: def_ciudad,
      });

      GS.cache = { el: temp };
      navigate("/sacramentos/" + type + "/" + def_id + "/new");
    } else if (action === "print") {
      const a = parsePrint(LS.showElement, type);
      window.open(
        "https://brian101016-projects.netlify.app/web/impresion/?" + a,
        "_blank"
      );
    } else {
      GS.cache = { el: LS.showElement };
      navigate("/sacramentos/" + type + "/" + id + "/" + action);
    }
  }

  // ------------------------------------------------------------------------------------ RESET ON TYPE CHANGE
  useEffect(() => {
    setLS({ showElement: res_one });
  }, [type, res_one]);

  // ------------------------------------------------------------------------------------ RETURN
  return (
    <div className={props.className + " screen"}>
      <Helmet>
        {LS.showElement ? (
          <title>Ver {LS.showElement.id} | RSA</title>
        ) : (
          <title>Sacramentos: {type} | RSA</title>
        )}
      </Helmet>

      <h1>{type}</h1>
      <ScrollToTopOnMount />

      {/* ------------------------------------------------------------------------------------ SHOWLIST */}
      <SearchTable
        key={type}
        _allowCollapse
        _allowOrder
        _allowSearch
        _booleans={{
          sexo: ["Hombre", "Mujer"],
        }}
        _headers={LABELS._SACRAMENTOS}
        _collection_title={type}
        _collection={res_all || []}
        _onSelect={(el, id) => {
          if (el.id === id) return;
          LS.showElement = el;
          navigate("/sacramentos/" + type + "/" + el.id);
        }}
      />

      {/* ------------------------------------------------------------------------------------ CRUD BUTTONS */}
      <div className="crud-buttons">
        <button
          className="primary big"
          onClick={() => handleControls("new")}
          disabled={!GS.currentUser?.permiso_sacramentos}
        >
          Crear nuevo registro
        </button>

        {LS.showElement && (
          <>
            <button
              className="secondary big"
              onClick={() => handleControls("edit")}
              disabled={!GS.currentUser?.permiso_sacramentos}
            >
              Editar registro
            </button>

            <button className="big" onClick={() => handleControls("print")}>
              Imprimir registro
            </button>

            <button
              className="danger big"
              onClick={() => handleControls("delete")}
              disabled={!GS.currentUser?.permiso_sacramentos}
            >
              Eliminar registro
            </button>
          </>
        )}
      </div>

      {/* ------------------------------------------------------------------------------------ FORMULARIO - VISUALIZAR */}
      {LS.showElement && (
        <SacramentosForm
          key={LS.showElement.id}
          _disabled
          _sacramento={type as "bautizos"}
          _store={LS.showElement}
        />
      )}
    </div>
  );
};

// ################################ STYLES ################################
const SacramentosScreen = styled(_SacramentosScreen).attrs(
  (props: SacramentosScreenProps): SacramentosScreenProps => {
    return { ...props };
  }
)`
  ${(props) => css`
    .crud-buttons {
      display: block;
      margin: 0.5rem 2.5rem;

      button {
        margin-left: 0;
      }
    }

    ${parseCSS(props._style)}
  `}
`;

// ################################ EXPORTS ################################
export default SacramentosScreen;
